/**
 * Copyright (c) Nicolas Gallagher.
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 *
 */
import React, { forwardRef, useMemo, useEffect } from 'react';
import { canUseDOM } from 'fbjs/lib/ExecutionEnvironment';
import View from '../View';
import StyleSheet from '../StyleSheet';
var ModalContent = /*#__PURE__*/ forwardRef(function (props, forwardedRef) {
  var active = props.active,
    children = props.children,
    onRequestClose = props.onRequestClose,
    transparent = props.transparent;
  useEffect(
    function () {
      if (canUseDOM) {
        var closeOnEscape = function closeOnEscape(e) {
          // 10_009 is for samsung back button
          // 8 is for x1 back button

          // allow backspace key when in text input
          if(e.target.tagName === 'INPUT' && (e.key === 'Back' || e.keyCode === 8))
            return;

          if (active && (e.key === 'Escape' || e.key === 'Back' || e.keyCode === 10009 || e.keyCode === 8)) {
            e.stopPropagation();

            if (onRequestClose) {
              onRequestClose();
            }
          }
        };

        document.addEventListener('keydown', closeOnEscape, true);
        return function () {
          return document.removeEventListener('keydown', closeOnEscape, true);
        };
      }
    },
    [active, onRequestClose]
  );
  var style = useMemo(
    function () {
      return [styles.modal, transparent ? styles.modalTransparent : styles.modalOpaque];
    },
    [transparent]
  );
  return /*#__PURE__*/ React.createElement(
    View,
    {
      accessibilityRole: active ? 'dialog' : null,
      'aria-modal': true,
      ref: forwardedRef,
      style: style
    },
    /*#__PURE__*/ React.createElement(
      View,
      {
        style: styles.container
      },
      children
    )
  );
});
var styles = StyleSheet.create({
  modal: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  },
  modalTransparent: {
    backgroundColor: 'transparent'
  },
  modalOpaque: {
    backgroundColor: 'white'
  },
  container: {
    top: 0,
    flex: 1
  }
});
export default ModalContent;
